import { LoadingPriorisationFormModel, LoadingPriorisationModel } from '../models'
import React, { useContext, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import {
  getWallboxLoadPrioSettings as getWallboxLoadPrioSettingsApi,
  updateWallboxLoadPrioSettings as updateWallboxLoadPrioSettingsApi,
} from 'src/Libs/wallboxes/api/wallboxes.api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { useFarmContext } from 'src/Elements/Farm'

interface UseWallboxesLoadPrioHook {
  isLoading: boolean
  isError: boolean
  loadPrioSettings: LoadingPriorisationModel | null
  refreshLoadPrioSettings: () => Promise<void>
  updateLoadPrioSettings: (model: LoadingPriorisationFormModel) => Promise<boolean>
}

/**
 * This hook return functions needed to display and manage the loading
 * prioritization for a wallbox.
 */
const useWallboxesLoadPrio = (storageSerialNo?: string): UseWallboxesLoadPrioHook => {
  const { t } = useCustomTranslation()

  const { enqueueErrorStack } = useSnackbar()
  const [loadPrioSettings, setLoadPrioSettings] = useState<LoadingPriorisationModel | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true

  const authFetch = useAuthFetchWithResponse()

  const refreshLoadPrioSettings = async (): Promise<void> => {
    if (isUndefined(storageSerialNo)) {
      throw new Error(`Invalid activeStorageId (id: ${storageSerialNo})`)
    }

    try {
      const result = await getWallboxLoadPrioSettingsApi(storageSerialNo, authFetch, !isServerOffline)

      if (!result.hasErrors) {
        setLoadPrioSettings(result.value as LoadingPriorisationModel)
      } else {
        enqueueErrorStack(t('api.wallbox.loadPriorisation.genericLoadError'))
      }
    } catch (e) {
      setLoadPrioSettings(null)
      enqueueErrorStack(t('api.wallbox.loadPriorisation.genericLoadError'))
    }
  }
  const updateLoadPrioSettings = async (model: LoadingPriorisationFormModel): Promise<boolean> => {
    if (isUndefined(storageSerialNo)) {
      throw new Error(`Invalid activeStorageId (id: ${storageSerialNo})`)
    }

    const result = await updateWallboxLoadPrioSettingsApi(storageSerialNo, model, authFetch)
    return result.hasErrors
  }

  React.useEffect(() => {
    if (storageSerialNo) {
      setIsLoading(true)
      refreshLoadPrioSettings()
        .catch(error => {
          setIsError(true)
          console.error(error)
          enqueueErrorStack(t('settings.wallbox.load-prio.load.error'))
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageSerialNo])

  return {
    isLoading,
    isError,
    loadPrioSettings,
    refreshLoadPrioSettings,
    updateLoadPrioSettings,
  }
}

export default useWallboxesLoadPrio
